import { Injectable } from '@angular/core';
import { updateRequestStatus } from '@ngneat/elf-requests';
import { take, tap } from 'rxjs/operators';
import {
	deleteAllEntities,
	deleteEntities,
	getAllEntities,
	resetActiveId,
	setEntities,
	upsertEntities
} from '@ngneat/elf-entities';
import { Observable, BehaviorSubject } from 'rxjs';

import {CuttingListLine } from '~shared/types';
import { AppRepository } from '~shared/store';
import { sortByUlid } from '~shared/helpers';
import { CuttingListService } from '~core/services/cutting-list.service';

import { cuttingListLineStore } from './cutting-list-lines.store';
import { cuttingListLinesSelector } from './cutting-list-lines.selectors';

@Injectable({ providedIn: 'root' })
export class CuttingListLinesRepository {
	// The line picked in the lines table, shown as a panel next to it
	public readonly selectedLineId$ = new BehaviorSubject<string | null>(null);
	public cuttingListLines$ = cuttingListLinesSelector.cuttingListLines$;
	public cuttingListLinesLoading$ = cuttingListLinesSelector.cuttingListLinesLoading$;
	public activeCuttingListLine$ = cuttingListLinesSelector.activeCuttingListLine$;
	// Lines typed but not saved yet, per cutting list, while its lines table is closed (e.g. a
	// visit to the materials module), so the user finds them back as they left them
	private readonly draftLines = new Map<string, Partial<CuttingListLine>[]>();

	constructor(
		private readonly cuttingListService: CuttingListService,
		private readonly appRepository: AppRepository
	) {}

	public getAll(cuttingListId: string): Observable<CuttingListLine[]> {
		cuttingListLineStore.update(updateRequestStatus('get-cutting-list-lines', 'pending'));
		return this.cuttingListService
			.getCuttingListLines(cuttingListId)
			.pipe(
				take(1),
				tap((cuttingListLines) => {
					cuttingListLineStore.update(
						upsertEntities(cuttingListLines),
						updateRequestStatus('get-cutting-list-lines', 'success')
					)
				})
			)
	}

	public create(cuttingListId: string, body): Observable<CuttingListLine> {
		cuttingListLineStore.update(updateRequestStatus('create-cutting-list-line', 'pending'));
		return this.cuttingListService
			.createCuttingListLine(cuttingListId, body)
			.pipe(
				take(1),
				tap((cuttingListLine) => {
					cuttingListLineStore.update(
						upsertEntities(cuttingListLine),
						updateRequestStatus('create-cutting-list-line', 'success')
					)
				})
			)
	}

	public update(cuttingListId: string, cuttingListLineId: string, body): Observable<CuttingListLine> {
		cuttingListLineStore.update(updateRequestStatus('update-cutting-list-line', 'pending'));
		return this.cuttingListService
			.updateCuttingListLine(cuttingListId, cuttingListLineId, body)
			.pipe(
				take(1),
				tap((cuttingListLine) => {
					cuttingListLineStore.update(
						upsertEntities(cuttingListLine),
						updateRequestStatus('update-cutting-list-line', 'success')
					)
				}),
				this.appRepository.withBackgroundLoading()
			)
	}

	public duplicate(cuttingListId: string, cuttingListLineId: string): Observable<CuttingListLine> {
		cuttingListLineStore.update(updateRequestStatus('duplicate-cutting-list-line', 'pending'));
		return this.cuttingListService
			.duplicateCuttingListLine(cuttingListId, cuttingListLineId)
			.pipe(
				take(1),
				tap((cuttingListLine) => {
					cuttingListLineStore.update(
						upsertEntities(cuttingListLine),
						updateRequestStatus('duplicate-cutting-list-line', 'success')
					)
				})
			)
	}

	public delete(cuttingListId: string, cuttingListLineId: string): Observable<void> {
		cuttingListLineStore.update(updateRequestStatus('delete-cutting-list-line', 'pending'));
		return this.cuttingListService
			.deleteCuttingListLine(cuttingListId, cuttingListLineId)
			.pipe(
				take(1),
				tap(() => {
					cuttingListLineStore.update(
						deleteEntities(cuttingListLineId),
						updateRequestStatus('delete-cutting-list-line', 'success')
					)
				})
			)
	}

	public clearCuttingListsLines(): void {
		cuttingListLineStore.update(
			deleteAllEntities(),
			resetActiveId(),
		)
	}

	public upsertLines(lines: CuttingListLine[]): void {
		cuttingListLineStore.update(upsertEntities(lines));
	}

	public queryCuttingListsLines(): CuttingListLine[] {
		return sortByUlid(cuttingListLineStore.query(getAllEntities()));
	}

	public keepDraftLines(cuttingListId: string, drafts: Partial<CuttingListLine>[]): void {
		if (drafts.length) {
			this.draftLines.set(cuttingListId, drafts);
		} else {
			this.draftLines.delete(cuttingListId);
		}
	}

	public takeDraftLines(cuttingListId: string): Partial<CuttingListLine>[] {
		const drafts = this.draftLines.get(cuttingListId) ?? [];
		this.draftLines.delete(cuttingListId);
		return drafts;
	}

	public reset() {
		cuttingListLineStore.reset();
	}
}
